import { Button, Card, CardBody, Chip } from "@nextui-org/react";
import {
  BarChart3,
  CheckCircle as CheckCircleIcon,
  HourglassIcon,
  LineChart as LineChartIcon,
  TriangleAlert as TriangleAlertIcon,
} from "lucide-react";
import { useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { StatsCard } from "../components/StatsCard";

const vendorsByRiskData = [
  { name: "High", value: 123, color: "#EC4899" },
  { name: "Medium", value: 270, color: "#F59E0B" },
  { name: "Low", value: 82, color: "#10B981" },
  { name: "Pending", value: 53, color: "#D1D5DB" },
];

const vendorsTrendData = [
  { month: "Jun", High: 98, Medium: 105, Low: 110, Pending: 130, All: 443 },
  { month: "Jul", High: 105, Medium: 110, Low: 115, Pending: 128, All: 458 },
  { month: "Aug", High: 110, Medium: 115, Low: 118, Pending: 125, All: 468 },
  { month: "Sep", High: 115, Medium: 118, Low: 120, Pending: 124, All: 477 },
  { month: "Oct", High: 120, Medium: 120, Low: 122, Pending: 123, All: 485 },
  { month: "Nov", High: 123, Medium: 123, Low: 123, Pending: 123, All: 492 },
];

const breachTrendData = [
  { month: "Jun", breaches: 5 },
  { month: "Jul", breaches: 6 },
  { month: "Aug", breaches: 3 },
  { month: "Sep", breaches: 7 },
  { month: "Oct", breaches: 4 },
  { month: "Nov", breaches: 4 },
];

const metricsData = [
  {
    label: "Total Vendors",
    value: 123,
    trend: {
      direction: "up" as const,
      value: 15,
    },
  },
  {
    label: "Vendors with Incidents (90 days)",
    value: 12,
    trend: {
      direction: "down" as const,
      value: 1,
    },
  },
  {
    label: "Vendors with Critical Business Impact",
    value: 17,
  },
];

const assessmentStages = [
  { label: "Intake Form", count: 87, icon: TriangleAlertIcon, color: "#F59E0B", bgColor: "#FEF3C7" },
  { label: "Due Diligence", count: 142, icon: HourglassIcon, color: "#3B82F6", bgColor: "#DBEAFE" },
  { label: "Review", count: 95, icon: TriangleAlertIcon, color: "#F59E0B", bgColor: "#FEF3C7" },
  { label: "Reassessment", count: 68, icon: HourglassIcon, color: "#3B82F6", bgColor: "#DBEAFE" },
  { label: "Completed", count: 136, icon: CheckCircleIcon, color: "#10B981", bgColor: "#D1FAE5" },
];

interface OverviewTabProps {
  onNavigateToBreachHistory: () => void;
}

export function OverviewTab({ onNavigateToBreachHistory }: OverviewTabProps) {
  const [chartType, setChartType] = useState<"bar" | "trend">("bar");

  return (
    <div className="space-y-4 grow flex flex-col">
      {/* Metrics */}
      <div className="grid grid-cols-3 gap-4">
        {metricsData.map((metric, index) => (
          <StatsCard key={index} label={metric.label} value={metric.value} trend={metric.trend} />
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-2 gap-6 flex-1 min-h-0">
        {/* Vendors by Risk Chart */}
        <Card className="shadow-none border border-[#E4E4E7] rounded-lg flex flex-col">
          <CardBody className="p-4 flex flex-col h-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-inter text-xs font-medium leading-5 tracking-normal text-[#71717A]">Vendors by Risk</h3>
              <div className="flex items-center gap-1 bg-[#F4F4F5] rounded p-1">
                <Button
                  isIconOnly
                  size="sm"
                  variant="light"
                  className={`min-w-6 w-6 h-6 ${chartType === "bar" ? "bg-white shadow-sm" : ""}`}
                  onPress={() => setChartType("bar")}
                >
                  <BarChart3 size={14} className={chartType === "bar" ? "text-[#006FEE]" : "text-[#71717A]"} />
                </Button>
                <Button
                  isIconOnly
                  size="sm"
                  variant="light"
                  className={`min-w-6 w-6 h-6 ${chartType === "trend" ? "bg-white shadow-sm" : ""}`}
                  onPress={() => setChartType("trend")}
                >
                  <LineChartIcon size={14} className={chartType === "trend" ? "text-[#006FEE]" : "text-[#71717A]"} />
                </Button>
              </div>
            </div>
            <div className="flex-1 min-h-[300px] overflow-hidden">
              <ResponsiveContainer width="100%" height="100%">
                {chartType === "bar" ? (
                  <BarChart data={vendorsByRiskData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 14, fill: "#71717A", fontWeight: 400 }}
                      axisLine={{ stroke: "#E4E4E7" }}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 12, fill: "#71717A" }}
                      axisLine={{ stroke: "#E4E4E7" }}
                      tickLine={false}
                      domain={[0, 300]}
                      ticks={[0, 50, 100, 150, 200, 250, 300]}
                    />
                    <Tooltip
                      cursor={{ fill: "rgba(0, 0, 0, 0.02)" }}
                      contentStyle={{
                        backgroundColor: "rgba(0, 0, 0, 0.8)",
                        backdropFilter: "blur(8px)",
                        border: "none",
                        borderRadius: "8px",
                        padding: "8px 12px",
                        fontSize: "12px",
                      }}
                      itemStyle={{
                        color: "white",
                      }}
                      labelStyle={{
                        color: "white",
                      }}
                      formatter={(value: any, name: any) => [value, name.charAt(0).toUpperCase() + name.slice(1)]}
                    />
                    <Bar
                      dataKey="value"
                      radius={[4, 4, 0, 0]}
                      barSize={40}
                      label={{
                        position: 'top',
                        fill: '#52525B',
                        fontSize: 14,
                        fontWeight: 400
                      }}
                    >
                      {vendorsByRiskData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                ) : (
                  <LineChart data={vendorsTrendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
                    <XAxis
                      dataKey="month"
                      tick={{ fontSize: 12, fill: "#71717A" }}
                      axisLine={{ stroke: "#E4E4E7" }}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 12, fill: "#71717A" }}
                      axisLine={{ stroke: "#E4E4E7" }}
                      tickLine={false}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "rgba(0, 0, 0, 0.8)",
                        backdropFilter: "blur(8px)",
                        border: "none",
                        borderRadius: "8px",
                        padding: "8px 12px",
                        fontSize: "12px",
                      }}
                      itemStyle={{
                        color: "white",
                      }}
                      labelStyle={{
                        color: "white",
                      }}
                      labelFormatter={(label: string) => {
                        const monthMap: { [key: string]: string } = {
                          "Jun": "June",
                          "Jul": "July",
                          "Aug": "August",
                          "Sep": "September",
                          "Oct": "October",
                          "Nov": "November"
                        };
                        return monthMap[label] || label;
                      }}
                    />
                    <Legend wrapperStyle={{ paddingTop: "20px", fontSize: "12px" }} iconType="line" />
                    <Line
                      type="monotone"
                      dataKey="All"
                      stroke="#3B82F6"
                      strokeWidth={3}
                      dot={{ fill: "#3B82F6", r: 4 }}
                    />
                    <Line type="monotone" dataKey="High" stroke="#EC4899" strokeWidth={2} dot={{ fill: "#EC4899", r: 3 }} />
                    <Line
                      type="monotone"
                      dataKey="Medium"
                      stroke="#F59E0B"
                      strokeWidth={2}
                      dot={{ fill: "#F59E0B", r: 3 }}
                    />
                    <Line type="monotone" dataKey="Low" stroke="#10B981" strokeWidth={2} dot={{ fill: "#10B981", r: 3 }} />
                    <Line
                      type="monotone"
                      dataKey="Pending"
                      stroke="#9CA3AF"
                      strokeWidth={2}
                      dot={{ fill: "#9CA3AF", r: 3 }}
                    />
                  </LineChart>
                )}
              </ResponsiveContainer>
            </div>
          </CardBody>
        </Card>

        {/* Breach Trend */}
        <Card className="shadow-none border border-[#E4E4E7] rounded-lg flex flex-col">
          <CardBody className="p-4 flex flex-col h-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-inter text-xs font-medium leading-5 tracking-normal text-[#71717A]">Breach Trend</h3>
              <Button size="sm" variant="light" color="primary" onPress={onNavigateToBreachHistory}>
                View All
              </Button>
            </div>
            <div className="flex-1 min-h-[300px] overflow-x-hidden">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={breachTrendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 14, fill: "#71717A", fontWeight: 400 }}
                    axisLine={{ stroke: "#E4E4E7" }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: "#71717A" }}
                    axisLine={{ stroke: "#E4E4E7" }}
                    tickLine={false}
                    domain={[0, 'auto']}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "rgba(0, 0, 0, 0.8)",
                      backdropFilter: "blur(8px)",
                      border: "none",
                      borderRadius: "8px",
                      padding: "8px 12px",
                      fontSize: "12px",
                    }}
                    itemStyle={{
                      color: "white",
                    }}
                    labelStyle={{
                      color: "white",
                    }}
                    formatter={(value: any) => [value, "Breaches"]}
                    labelFormatter={(label: string) => {
                      const monthMap: { [key: string]: string } = {
                        "Jun": "June",
                        "Jul": "July",
                        "Aug": "August",
                        "Sep": "September",
                        "Oct": "October",
                        "Nov": "November"
                      };
                      return monthMap[label] || label;
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="breaches"
                    stroke="#EC4899"
                    strokeWidth={3}
                    dot={{ fill: "#EC4899", r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Assessments by Stage */}
      <div className="!mb-4">
        <h3 className="font-inter text-sm font-medium leading-5 tracking-normal mb-4 text-[#71717A]">Assessments by Stage</h3>
        <div className="grid grid-cols-5 gap-4">
          {assessmentStages.map((stage, index) => {
            const Icon = stage.icon;
            return (
              <Card
                key={index}
                isPressable
                className="shadow-none border border-[#E4E4E7] rounded-lg cursor-default"
              >
                <CardBody className="p-4">
                  <div className="font-inter text-lg font-medium leading-7 tracking-normal mb-3 text-[#171717]">{stage.count}</div>
                  <div className="flex items-center gap-2">
                    <Chip
                      variant="bordered"
                      radius="full"
                      className="h-5 w-auto border-1"
                      classNames={{
                        content: "px-1 py-2 flex items-center gap-1",
                      }}
                      style={{
                        color: stage.color,
                        borderColor: stage.color,
                        backgroundColor: stage.bgColor,
                      }}
                    >
                      <Icon size={13} />
                    </Chip>
                    <span className="font-inter text-sm font-normal leading-5 tracking-normal">{stage.label}</span>
                  </div>
                </CardBody>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}
